/* eslint-disable import/no-deprecated */
/**
 * Container for AppComponent
 *
 * @module containers/App/AppContainer
 */
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { createSelector } from 'reselect';

import * as Actions from '../../actions';
import { showRedirectedListenerCoachmark } from '../../actions/Listener';
import AppComponent from '../../components/App/App';
import determineShouldDropSearchToNextLine from '../../components/SearchResponsiveHelper/SearchResponsiveHelper';
import * as Constants from '../../constants';
import { StartupQueryParameterKeys } from '../../constants/App';
import { ApplicationStateKeys } from '../../constants/ApplicationStateKeys';
import { BrowserSupportLevels } from '../../constants/ClientInfo';
import OnboardingContainer from '../../containers/OnboardingContainer/OnboardingContainer';
import { getInDomain } from '../../domains/externalUtils';
import { getStore } from '../../store/StoreAccessor';
import * as Utils from '../../utils';

const shouldShowSplash = createSelector(
  state => state.application.get(ApplicationStateKeys.APPLICATION_INITIALIZED),
  isInitialized => {
    return !isInitialized;
  },
);

/**
 * Show tuner if there is a current station
 *
 */
const shouldShowTuner = createSelector(
  Utils.Listener.isAnonymousListener,
  state => {
    return !!getInDomain('playback.utils.getCurrentPlaylist', false)(state);
  },
  state => {
    return getInDomain('playback.utils.listenerHasStations', false)(state);
  },
  Utils.Application.is404Page,
  Utils.Capabilities.isMobileDevice,
  Utils.ClientInfo.isWindowsClient,
  Utils.Listener.getJustMigratedWindowsAccount,
  Utils.Application.getIsLoggingOut,
  Utils.Application.shouldNavigateToCommunity,
  (state, props) => {
    return Utils.Route.getCurrentPathname(props);
  },
  (
    isAnon,
    isCurrentlyPlaying,
    hasStations,
    is404,
    isMobileDevice,
    isWindowsClient,
    justMigratedWindowsAccount,
    isLoggingOut,
    communitySSO,
    pathname,
  ) => {
    if (
      is404 ||
      isMobileDevice ||
      (isWindowsClient && justMigratedWindowsAccount) ||
      isLoggingOut ||
      communitySSO
    ) {
      return false;
    } else if (
      Utils.Route.isPartnerUpgradePath(pathname) ||
      Utils.Route.isDesktopLandingPath(pathname)
    ) {
      return false;
    } else if (isCurrentlyPlaying) {
      return true;
    } else if (hasStations) {
      if (Utils.Route.pathMatchesAccount(pathname)) {
        return isAnon;
      }
      return true;
    }

    /* always show under Now Playing */
    return Utils.Route.isPathInNowPlayingPath(pathname);
  },
);

/**
 * Show top nav except in onboarding pages
 */
const shouldShowTopNav = createSelector(
  Utils.Listener.isAnonymousListener,
  Utils.Application.is404Page,
  Utils.Application.isPlaylistPage,
  Utils.Capabilities.isMobileDevice,
  Utils.Route.isOnUpgradeOrPaymentsPage,
  Utils.Route.isOnPlansPage,
  (state, props) => {
    return Utils.Route.getCurrentPathname(props);
  },
  (
    isAnon,
    is404,
    isPlaylist,
    isMobileDevice,
    isOnUpgradeOrPaymentsPage,
    isOnPlansPage,
    pathname,
  ) => {
    if (isMobileDevice) {
      return false;
    }

    if ((is404 || isOnUpgradeOrPaymentsPage || isOnPlansPage) && isAnon) {
      return false;
    }

    if (
      Utils.Route.pathMatchesAccount(pathname) ||
      pathname === Utils.Route.getPremiumWelcomePath() ||
      Utils.Route.isPartnerUpgradePath(pathname)
    ) {
      return false;
    }

    return true;
  },
);

/**
 * Show Anonymous Welcome panel for anon listeners except for when in onboarding pages, podcast pages & upgrades
 */
const shouldShowAnonWelcome = createSelector(
  state => {
    return Utils.Listener.isAnonymousListener(state);
  },
  state => {
    return getInDomain('playback.utils.isStationNarrativeDisplayed', false)(state);
  },
  (state, props) => {
    return Utils.Route.getCurrentPathname(props);
  },
  (isAnon, stationNarrative, pathname) => {
    return (
      !Utils.Route.pathMatchesAccount(pathname) &&
      pathname !== Utils.Route.getPremiumWelcomePath() &&
      !Utils.Route.isPodcastPath(pathname) &&
      isAnon &&
      !stationNarrative
    );
  },
);

/**
 * Show FTUX Welcome Cold start for premium user.
 */
const shouldShowColdStart = createSelector(
  state => {
    return Utils.Listener.isAnonymousListener(state);
  },
  state => {
    return Utils.Listener.hasSeenWebPremiumWelcome(state);
  },
  state => {
    return getInDomain('playback.utils.listenerHasStations', false)(state);
  },
  state => {
    return getInDomain('collection.utils.collectionHasItems', false)(state);
  },
  state => {
    return Utils.Listener.hasPremiumFeatures(state);
  },
  () => {
    return window.location.pathname;
  },
  (isAnon, seenWebPremiumWelcome, hasStation, collectionHasItems, isOnDemandEnabled, pathname) => {
    const isCollectionOrSearchPath =
      Utils.Route.isMyMusicPath(pathname) ||
      Utils.Route.isCollectionPath(pathname) ||
      Utils.Route.isInSearchPath(pathname);
    return (
      !isAnon &&
      !seenWebPremiumWelcome &&
      !hasStation &&
      !collectionHasItems &&
      isOnDemandEnabled &&
      isCollectionOrSearchPath
    );
  },
);

/**
 * whether to display the search field in the header. In case of the myStations path,
 * and no stations, the search field is not be present in the header.
 */
const shouldShowSearchInHeader = createSelector(
  state => {
    return getInDomain('playback.utils.listenerHasStations', false)(state);
  },
  state => {
    return Utils.UIState.getIsSearchOpen(state);
  },
  (state, props) => {
    return Utils.Route.getCurrentPathname(props);
  },
  state => {
    return shouldShowColdStart(state);
  },
  state => {
    return getInDomain('collection.utils.collectionHasItems', false)(state);
  },
  state => {
    return Utils.Listener.listenerIsPremium(state);
  },
  (hasStations, isSearchOpen, pathname, showColdStart, hasContent, isPremium) => {
    const isCollectionPath = pathname.indexOf(Utils.Route.getCollectionPath()) !== -1;
    const isMyStationsPath = pathname.indexOf(Utils.Route.getMyStationsPath()) !== -1;
    const isCongratulationsPath = pathname.indexOf(Utils.Route.getCongratulationsPath()) !== -1;

    if (isSearchOpen || showColdStart) {
      return true;
    }

    // Non collUni stations, collections or congratulations path with empty collection, without cold start
    if (
      (isMyStationsPath || isCollectionPath || isCongratulationsPath) &&
      !hasStations &&
      !hasContent &&
      !isPremium
    ) {
      return false;
    }

    return true;
  },
);

function shouldIdleRedirectToNowPlaying(state) {
  const isPlaybackEnabled = Utils.Capabilities.isPlaybackEnabled(state);
  const isCollectionEmpty = getInDomain('collection.utils.isCollectionEmpty', true)(state);
  return (
    isPlaybackEnabled &&
    Utils.Listener.listenerIsBasic(state) &&
    Utils.Route.pathRedirectsToNowPlayingIfInactive(window.location.pathname) &&
    !isCollectionEmpty
  );
}

export function mapStateToProps(state, ownProps) {
  const shouldShowDisplayAdsToT2 = getInDomain(
    'displayAds.utils.getShouldShowDisplayAdsToT2',
    false,
  )(state);
  const isPlus = Utils.Listener.listenerIsPlus(state);
  const showDisplayAds =
    Utils.Listener.shouldShowDisplayAds(state) || (shouldShowDisplayAdsToT2 && isPlus);
  const showAnonWelcome = shouldShowAnonWelcome(state, ownProps);
  const showSplashScreen = shouldShowSplash(state);
  const showTuner = shouldShowTuner(state, ownProps);
  const showTopNav = shouldShowTopNav(state, ownProps);
  const showLanding = Utils.Route.shouldShowLanding(state);
  const showSearchInHeader = shouldShowSearchInHeader(state, ownProps);
  const locale = Utils.i18n.getLocale(state);
  const defaultLocale = Utils.i18n.getDefaultLocale();
  const i18nMessages = Utils.i18n.getMessagesSelector(state);
  const coachmarkPath = state.application.get(ApplicationStateKeys.COACHMARK_PATH);
  const listenerTier = Utils.Listener.getBranding(state);
  const isUserIdle = getInDomain('application.utils.idleStatusIsInactive')(state);
  const isRightRailActive = getInDomain('ads.utils.getIsRightRailActive', false)(state);
  const shouldUseWakeLock = getInDomain('playback.utils.shouldUseWakeLock', false)(state);
  const isStatsTestLoggerEnabled = Utils.TunerVars.isStatsTestLoggerEnabled(state);
  const shouldLoadSimStream = !Utils.Listener.shouldCurrentListenerGetAds(state);
  const nowPlayingPath = Utils.Route.getNowPlayingPath(state);
  const shouldLoadRewards = Utils.Listener.shouldCurrentListenerGetAds(state);
  const showColdStart = shouldShowColdStart(state);
  const isDebugMenuEnabled = Utils.DebugSettings.isDebugMenuEnabled();
  const shouldSkipStatsLogging = Utils.DebugSettings.shouldSkipStatsLogging();
  const shouldShowPremiumFeatures = Utils.Listener.hasPremiumFeatures(state);

  return {
    shouldIdleRedirectToNowPlaying: shouldIdleRedirectToNowPlaying(state),
    shouldRedirectToUnsupportedBrowser:
      Utils.ClientInfo.getBrowserSupport(state) === BrowserSupportLevels.NONE,
    showDisplayAds,
    shouldLoadSimStream,
    shouldLoadRewards,
    showAnonWelcome,
    showColdStart,
    showSplashScreen,
    locale,
    i18nMessages,
    showTuner,
    showTopNav,
    showLanding,
    showSearchInHeader,
    isDebugMenuEnabled,
    coachmarkPath,
    listenerTier,
    isUserIdle,
    unsupportedBrowserUrl: Utils.Route.getUnsupportedBrowserUrl(state),
    isListenerSupportMenuEnabled: !!Utils.QueryParams.get(
      state,
      StartupQueryParameterKeys.ENABLE_LISTENER_SUPPORT_MENU,
    ),
    isRightRailActive,
    defaultLocale,
    shouldUseWakeLock,
    isStatsTestLoggerEnabled,
    shouldShowPremiumFeatures,
    nowPlayingPath,
    shouldSkipStatsLogging,
    areVideoAdsEnabled:
      Utils.Listener.areVideoAdsEnabledForListener(state) ||
      getInDomain('rewards.utils.hasOpenCoachMark', false)(state),
    isAnonymousListener: Utils.Listener.isAnonymousListener(state),
    isSearchOpen: Utils.UIState.getIsSearchOpen(state),
  };
}

export const mapDispatchToProps = {
  componentClicked: Actions.Integrations.componentClicked,
  componentViewed: Actions.Integrations.componentViewed,
};

class AppContainer extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    showDisplayAds: PropTypes.bool.isRequired,
    showAnonWelcome: PropTypes.bool,
    showSplashScreen: PropTypes.bool,
    showTuner: PropTypes.bool,
    showTopNav: PropTypes.bool,
    showLanding: PropTypes.bool,
    shouldLoadSimStream: PropTypes.bool.isRequired,
    shouldLoadRewards: PropTypes.bool.isRequired,
    shouldShowPremiumWelcome: PropTypes.bool,
    showSearchInHeader: PropTypes.bool,
    showOutdatedBrowserBar: PropTypes.bool,
    locale: PropTypes.string,
    i18nMessages: PropTypes.object,
    isUserIdle: PropTypes.bool,
    shouldIdleRedirectToNowPlaying: PropTypes.bool.isRequired,
    shouldRedirectToUnsupportedBrowser: PropTypes.bool.isRequired,
    isDebugMenuEnabled: PropTypes.bool.isRequired,
    coachmarkPath: PropTypes.string,
    listenerTier: PropTypes.string,
    isRightRailActive: PropTypes.bool,
    inMiniMode: PropTypes.bool,
    params: PropTypes.shape({
      stationId: PropTypes.string,
    }),
    unsupportedBrowserUrl: PropTypes.string,
    shouldUseWakeLock: PropTypes.bool,
    shouldShowPremiumFeatures: PropTypes.bool,
    nowPlayingPath: PropTypes.string.isRequired,
    isSearchOpen: PropTypes.bool,
    shouldDropSearchToNextLine: PropTypes.bool,
    componentClicked: PropTypes.func,
    componentViewed: PropTypes.func,
    router: PropTypes.object.isRequired,
  };

  componentWillMount() {
    const appContext = Utils.Context.createAppContext({
      store: getStore(),
      router: this.props.router,
      integrationActions: {
        clicked: this.props.componentClicked,
        viewed: this.props.componentViewed,
      },
    });
    this.setState(appContext);
  }

  componentDidMount() {
    Utils.Time.recordTimeAtMoment(Constants.App.TimingMoments.FIRST_PAINT);
    if (this.props.coachmarkPath) {
      this.state.store.dispatch(showRedirectedListenerCoachmark(this.props.coachmarkPath));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.router) {
      if (nextProps.shouldRedirectToUnsupportedBrowser) {
        window.location.href = this.props.unsupportedBrowserUrl;
      }
      if (
        nextProps.isUserIdle &&
        !this.props.isUserIdle &&
        this.props.shouldIdleRedirectToNowPlaying
      ) {
        // user just became idle on a page that we don't want to keep them on. redirect
        this.props.router.push(this.props.nowPlayingPath);
      }
    }
  }

  render() {
    let toRender = <AppComponent {...this.props} />;

    if (this.props.showLanding) {
      toRender = <OnboardingContainer {...this.props} />;
    }

    return (
      <Utils.Context.AppContext.Provider value={this.state}>
        {toRender}
      </Utils.Context.AppContext.Provider>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(determineShouldDropSearchToNextLine(AppContainer)));
